import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

interface EditLevelPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  level: {
    id: number;
    code: string;
    name: string;
    notes?: string;
  } | null;
  isParentLevel: boolean;
  onSave: (id: number, name: string, notes: string) => void;
}

export function EditLevelPanel({
  open,
  onOpenChange,
  level,
  isParentLevel,
  onSave,
}: EditLevelPanelProps) {
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);

  // Initialize form when level changes or panel opens
  useEffect(() => {
    if (open && level) {
      setName(level.name);
      setNotes(level.notes || '');
      setErrors({});
      setHasChanges(false);
    }
  }, [open, level]);

  // Track changes
  useEffect(() => {
    if (level) {
      const nameChanged = name !== level.name;
      const notesChanged = notes !== (level.notes || '');
      setHasChanges(nameChanged || notesChanged);
    }
  }, [name, notes, level]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Name validation
    if (!name.trim()) {
      newErrors.name = 'This field is required';
    } else if (name.length > 100) {
      newErrors.name = 'Maximum 100 characters allowed';
    }

    // Notes validation
    if (notes.length > 500) {
      newErrors.notes = 'Maximum 500 characters allowed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!level) return;

    if (validateForm()) {
      onSave(level.id, name.trim(), notes.trim());
      toast.success('Level updated successfully', {
        duration: 3000,
      });
      onOpenChange(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      setUnsavedDialogOpen(true);
    } else {
      onOpenChange(false);
    }
  };

  const handleDiscardChanges = () => {
    setUnsavedDialogOpen(false);
    onOpenChange(false);
  };

  const handleBlur = (field: string) => {
    // Validate single field on blur
    const newErrors = { ...errors };

    if (field === 'name') {
      if (!name.trim()) {
        newErrors.name = 'This field is required';
      } else if (name.length > 100) {
        newErrors.name = 'Maximum 100 characters allowed';
      } else {
        delete newErrors.name;
      }
    } else if (field === 'notes') {
      if (notes.length > 500) {
        newErrors.notes = 'Maximum 500 characters allowed';
      } else {
        delete newErrors.notes;
      }
    }

    setErrors(newErrors);
  };

  const isValid = name.trim() && Object.keys(errors).length === 0;
  const nameCharCount = name.length;
  const notesCharCount = notes.length;

  if (!level) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent className="sm:max-w-[540px] p-0 flex flex-col">
          <SheetHeader className="px-6 pt-6 pb-4">
            <SheetTitle>
              {isParentLevel ? 'Edit Parent Level' : 'Edit Child Level'}
            </SheetTitle>
            <SheetDescription>
              Code: <span className="font-medium text-foreground">{level.code}</span>
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="space-y-6">
                {/* Code Field (Read-Only) */}
                <div className="space-y-2">
                  <Label htmlFor="code">Code</Label>
                  <Input
                    id="code"
                    value={level.code}
                    disabled
                    className="bg-muted cursor-not-allowed"
                  />
                  <p className="text-sm text-muted-foreground">
                    Code cannot be changed
                  </p>
                </div>

                {/* Name Field (Required) */}
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    placeholder="Enter level name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={() => handleBlur('name')}
                    className={errors.name ? 'border-destructive' : ''}
                    autoFocus
                  />
                  {errors.name ? (
                    <p className="text-sm text-destructive">{errors.name}</p>
                  ) : nameCharCount >= 80 ? (
                    <p className="text-sm text-muted-foreground">
                      {nameCharCount}/100 characters
                    </p>
                  ) : null}
                </div>

                {/* Notes Field (Optional) */}
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    placeholder="Optional notes about this level..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    onBlur={() => handleBlur('notes')}
                    rows={3}
                    className={errors.notes ? 'border-destructive' : ''}
                  />
                  {errors.notes ? (
                    <p className="text-sm text-destructive">{errors.notes}</p>
                  ) : notesCharCount >= 400 ? (
                    <p className="text-sm text-muted-foreground">
                      {notesCharCount}/500 characters
                    </p>
                  ) : null}
                </div>
            </div>
          </div>

          <SheetFooter className="sticky bottom-0 bg-background border-t px-6 py-4 mt-auto flex-row justify-between gap-2 sm:justify-between">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!isValid || !hasChanges}>
              Save Changes
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={unsavedDialogOpen} onOpenChange={setUnsavedDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to discard them?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setUnsavedDialogOpen(false)}>
              Continue Editing
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDiscardChanges}>
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
